import { Trans } from '@lingui/react';
import AlertText from 'components/AlertText';
import { BasePageProps } from 'modules/makePage';
import { ProjectProvider } from 'modules/project/providers/project';
import React from 'react';
import {
  ReticulationDataDto,
  ReticulationDataImportDto,
  useDeleteStopeMutation,
  useUpdateMineModelMutation
} from 'providers/api';
import { usePage } from 'providers/page';
import useScenario from 'modules/project/providers/scenario/use';
import { useLocation } from 'react-router-dom';
import { ProjectAreaQueries } from '../../useProjectAreaQueries';
import Stage from '../Stage';
import { useModelState } from '../hooks';
import { SceneManagerProvider } from '../hooks/SceneManagerProvider';

const MAX_UNDO_ITEMS = 10;
const HISTORY_DEBOUNCE_MS = 1000;

const cloneSnapshot = (snapshot: ReticulationDataImportDto): ReticulationDataImportDto => JSON.parse(JSON.stringify(snapshot));
const getSnapshotKey = (snapshot: ReticulationDataImportDto): string => JSON.stringify({
  ...snapshot,
  date: null,
});
const getStopeShapeKey = (reticulationData: ReticulationDataDto): string => JSON.stringify(
  reticulationData.stopes.map((stope) => [
    stope.stopeId,
    stope.stopeGeometryFile?.path,
    stope.startDate?.toString(),
    stope.endDate?.toString(),
  ]),
);
const getSnapshotStopeNodeMap = (snapshot: ReticulationDataImportDto): Map<string, string> => new Map(
  snapshot.stope_data
    .filter((stope) => !!stope.STOPE_ID && !!stope.NODE_ID)
    .map((stope) => [stope.STOPE_ID, stope.NODE_ID as string]),
);

const toReticulationImportSnapshot = (reticulationData: ReticulationDataDto): ReticulationDataImportDto => {
  const stopeDataById = new Map(reticulationData.stopes.map((stope) => [stope.stopeId, stope]));
  const stopeData: ReticulationDataImportDto['stope_data'] = reticulationData.stopeLocations.map((location) => {
    const stope = stopeDataById.get(location.stopeId);

    return {
      STOPE_ID: location.stopeId,
      STOPE_NAME: stope?.stopeName,
      NODE_ID: location.nodeId,
      pipes: (stope?.pipeArrays ?? []).map((pipeArray) => ({
        pipe_id: pipeArray.pipeId?.value,
        pipe_vertical_length: pipeArray.pipeVerticalLength,
        pipe_horizontal_length: pipeArray.pipeHorizontalLength,
      })),
      PIPE_ID: stope?.pipeArrays?.[0]?.pipeId?.value,
      volume: stope?.volume,
      level: stope?.level,
      area: stope?.area,
      pour_preset_group_name: stope?.pourPresetGroupId,
      reference_1: stope?.references?.[0],
      reference_2: stope?.references?.[1],
      reference_3: stope?.references?.[2],
      reference_4: stope?.references?.[3],
      reference_5: stope?.references?.[4],
    };
  });

  return {
    date: new Date(),
    pipe_data: reticulationData.pipeData.map((pipe) => ({
      PIPE_ID: pipe.pipeId,
      name: pipe.name,
      class: pipe.pipeClass,
      inside_diameter: pipe.insideDiameter,
      minimum_v: pipe.minimumVelocity,
      maximum_v: pipe.maximumVelocity,
      p_rating: pipe.pressureRating,
      p_minimum: pipe.minimumPressure,
      colour: pipe.colour,
      starting_thickness: pipe.startingThickness,
      minimum_thickness: pipe.minimumThickness,
      calculation_mode: pipe.calculationMode,
      dimension_standard: pipe.dimensionStandard,
      pipe_size: pipe.pipeSize,
      wall_selection: pipe.wallSelection,
      outside_diameter: pipe.outsideDiameter,
      wall_thickness: pipe.wallThickness,
      lining_thickness: pipe.liningThickness,
      wear_allowance: pipe.wearAllowance,
      material_standard: pipe.materialStandard,
      material_grade: pipe.materialGrade,
      pressure_rating_code: pipe.pressureRatingCode,
      factor_of_safety: pipe.factorOfSafety,
      weld_type: pipe.weldType,
      coupling_standard: pipe.couplingStandard,
      coupling_class: pipe.couplingClass,
      cut_depth: pipe.cutDepth,
      pipe_pressure_rating: pipe.pipePressureRating,
      coupling_pressure_rating: pipe.couplingPressureRating,
      design_temperature: pipe.designTemperature,
      pipe_roughness: pipe.pipeRoughness,
    })),
    // A node is coordinates plus its own attributes; every edge — its pipe, its flow direction and its
    // flow-loop length — is a spool. Both lists below are exhaustive rebuilds of their DTO, so a field
    // omitted here is silently nulled on every undo: enumerate, never spread.
    node_data: reticulationData.nodes.map((node) => ({
      NODE_ID: node.nodeId,
      // Omitting this renamed every node to its id on undo. nodeName reads back as the id for an unnamed
      // node, which ModelNode normalises back to unnamed, so a named node keeps its name and an unnamed one
      // stays unnamed.
      NODE_NAME: node.nodeName,
      x: node.point.x,
      y: node.point.y,
      z: node.point.z,
      stope: node.isStope,
      PIPE_ID: node.pipeId,
      pit_name: node.pit,
      pit: node.isPit,
      area: node.area,
      level: node.level,
      booster_pressure: node.boosterPressure ?? 0,
      rupture_disk_pressure: node.ruptureDiskPressure ?? 0,
      choke_head: node.chokeHead ?? 0,
      plant_node: node.isPlantNode ?? false,
      total_volume_through_node: node.totalVolumeThroughNode,
      total_dry_tonnes_through_node: node.totalDryTonnesThroughNode,
    })),
    spool_data: reticulationData.spools.map((spool) => ({
      SPOOL_ID: spool.spoolId,
      nodeAId: spool.nodeAId,
      nodeBId: spool.nodeBId,
      pipeId: spool.pipeId,
      allowFlowAB: spool.allowFlowAB,
      allowFlowBA: spool.allowFlowBA,
      chokeLength: spool.chokeLength,
      // Throughput history lives on the spool. Omitting these zeroes every spool's wear history on undo.
      totalVolumeThroughSpool: spool.totalVolumeThroughSpool,
      totalDryTonnesThroughSpool: spool.totalDryTonnesThroughSpool,
      // Omitting these wipes every spool's name, dates and status on undo, and hides a status change from the snapshot key.
      name: spool.name,
      installedOn: spool.installedOn,
      removedOn: spool.removedOn,
      status: spool.status,
      other: spool.other,
      level: spool.level,
      area: spool.area,
    })),
    stope_data: stopeData,
  };
};

const ProjectStopesPage = ({ queries }: BasePageProps<ProjectAreaQueries>) => {
  const [
    projectQuery,
    mineModelQuery,
    mineOverviewQuery,
    // poursQuery is not used but is required in order to access inspection points
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    poursQuery,
  ] = queries;

  const project = projectQuery?.data;
  const mineModelDto = mineModelQuery?.data;
  const mineOverview = mineOverviewQuery?.data;
  const location = useLocation();
  const path = location.pathname;
  const url = location.pathname;
  const modelState = useModelState();
  const deleteStopeMutation = useDeleteStopeMutation();
  const updateMineModelMutation = useUpdateMineModelMutation(project?.entityId ?? '');
  const {
    actions: { setUndoAction },
  } = usePage();
  const { activeScenarioId, isSwitching } = useScenario();
  const [, setHistoryVersion] = React.useState(0);
  const [isUndoing, setIsUndoing] = React.useState(false);
  const historyRef = React.useRef<ReticulationDataImportDto[]>([]);
  const previousSnapshotRef = React.useRef<ReticulationDataImportDto | null>(null);
  const previousSnapshotKeyRef = React.useRef<string | null>(null);
  const pendingPushRef = React.useRef<ReticulationDataImportDto | null>(null);
  const pushDebounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isApplyingUndoRef = React.useRef(false);
  const isUndoingRef = React.useRef(false);
  const isSwitchingRef = React.useRef(isSwitching);
  const projectRef = React.useRef(project);
  const mineModelQueryRef = React.useRef(mineModelQuery);
  const deleteStopeMutationRef = React.useRef(deleteStopeMutation);
  const updateMineModelMutationRef = React.useRef(updateMineModelMutation);
  const currentScopeKeyRef = React.useRef<string | null>(null);
  const appliedProjectIdRef = React.useRef<string | null>(null);
  const appliedReticulationKeyRef = React.useRef<string | null>(null);
  const appliedStopeShapeKeyRef = React.useRef<string | null>(null);
  const initModelStateRef = React.useRef(modelState.initModelState);
  const loadedRef = React.useRef(modelState.loaded);
  const updateMineOverviewRef = React.useRef(modelState.updateMineOverview);

  initModelStateRef.current = modelState.initModelState;
  loadedRef.current = modelState.loaded;
  updateMineOverviewRef.current = modelState.updateMineOverview;
  isUndoingRef.current = isUndoing;
  isSwitchingRef.current = isSwitching;
  projectRef.current = project;
  mineModelQueryRef.current = mineModelQuery;
  deleteStopeMutationRef.current = deleteStopeMutation;
  updateMineModelMutationRef.current = updateMineModelMutation;

  const currentReticulationSnapshot = React.useMemo(() => {
    if (!mineModelDto?.reticulationData) {
      return null;
    }

    return toReticulationImportSnapshot(mineModelDto.reticulationData);
  }, [mineModelDto?.reticulationData]);

  const reticulationSnapshotKey = React.useMemo(() => {
    if (!currentReticulationSnapshot) {
      return null;
    }

    return getSnapshotKey(currentReticulationSnapshot);
  }, [currentReticulationSnapshot]);

  const stopeShapeKey = React.useMemo(
    () => (mineModelDto?.reticulationData ? getStopeShapeKey(mineModelDto.reticulationData) : null),
    [mineModelDto?.reticulationData],
  );

  const undoScopeKey = `${project?.entityId ?? ''}::${activeScenarioId ?? ''}`;

  React.useEffect(() => {
    if (currentScopeKeyRef.current === undoScopeKey) {
      return;
    }

    currentScopeKeyRef.current = undoScopeKey;
    historyRef.current = [];
    previousSnapshotRef.current = null;
    previousSnapshotKeyRef.current = null;
    pendingPushRef.current = null;
    if (pushDebounceTimerRef.current) {
      clearTimeout(pushDebounceTimerRef.current);
      pushDebounceTimerRef.current = null;
    }
    isApplyingUndoRef.current = false;
    appliedReticulationKeyRef.current = null;
    appliedStopeShapeKeyRef.current = null;
    appliedProjectIdRef.current = null;
    isUndoingRef.current = false;
    setIsUndoing(false);
    setHistoryVersion((v) => v + 1);
  }, [undoScopeKey]);

  React.useEffect(() => {
    if (!project || !mineModelDto || !mineOverview) {
      return;
    }

    const isProjectChanged = appliedProjectIdRef.current !== project.entityId;
    const isReticulationChanged = appliedReticulationKeyRef.current !== reticulationSnapshotKey;
    const isStopeShapeChanged = appliedStopeShapeKeyRef.current !== stopeShapeKey;

    if (!loadedRef.current() || isProjectChanged || isReticulationChanged || isStopeShapeChanged) {
      initModelStateRef.current(project, mineModelDto, mineOverview);
      appliedProjectIdRef.current = project.entityId;
      appliedReticulationKeyRef.current = reticulationSnapshotKey;
      appliedStopeShapeKeyRef.current = stopeShapeKey;
    }
  }, [mineModelDto, mineOverview, project, reticulationSnapshotKey, stopeShapeKey]);

  React.useEffect(() => {
    if (mineOverview && loadedRef.current()) {
      updateMineOverviewRef.current(mineOverview);
    }
  }, [mineOverview]);

  const commitPendingPush = React.useCallback(() => {
    if (pushDebounceTimerRef.current) {
      clearTimeout(pushDebounceTimerRef.current);
      pushDebounceTimerRef.current = null;
    }
    if (!pendingPushRef.current) return;
    historyRef.current.push(pendingPushRef.current);
    if (historyRef.current.length > MAX_UNDO_ITEMS) {
      historyRef.current.shift();
    }
    pendingPushRef.current = null;
    setHistoryVersion((v) => v + 1);
  }, []);

  React.useEffect(() => {
    if (!currentReticulationSnapshot || !reticulationSnapshotKey) return;

    // While a scenario switch is in flight the reticulation key changes because the data is
    // being replaced (not edited). Re-baseline against the incoming scenario instead of
    // recording it as an undoable edit, otherwise one scenario's state leaks into another's history.
    if (isSwitchingRef.current) {
      previousSnapshotKeyRef.current = reticulationSnapshotKey;
      previousSnapshotRef.current = currentReticulationSnapshot;
      return;
    }

    if (!previousSnapshotKeyRef.current) {
      previousSnapshotKeyRef.current = reticulationSnapshotKey;
      previousSnapshotRef.current = currentReticulationSnapshot;
      return;
    }

    if (reticulationSnapshotKey !== previousSnapshotKeyRef.current) {
      if (!isApplyingUndoRef.current && previousSnapshotRef.current) {
        if (!pendingPushRef.current) {
          pendingPushRef.current = cloneSnapshot(previousSnapshotRef.current);
        }
        if (pushDebounceTimerRef.current) {
          clearTimeout(pushDebounceTimerRef.current);
        }
        pushDebounceTimerRef.current = setTimeout(() => {
          commitPendingPush();
        }, HISTORY_DEBOUNCE_MS);
      }

      if (isApplyingUndoRef.current) {
        isApplyingUndoRef.current = false;
      }

      previousSnapshotKeyRef.current = reticulationSnapshotKey;
    }

    previousSnapshotRef.current = currentReticulationSnapshot;
  }, [currentReticulationSnapshot, reticulationSnapshotKey, commitPendingPush]);

  React.useEffect(() => () => {
    if (pushDebounceTimerRef.current) {
      clearTimeout(pushDebounceTimerRef.current);
      pushDebounceTimerRef.current = null;
    }
  }, []);

  const canUndo = (historyRef.current.length > 0 || pendingPushRef.current !== null) && !isUndoing && !isSwitching;

  const handleUndo = React.useCallback(async () => {
    const currentProject = projectRef.current;
    if (!currentProject || isUndoingRef.current || isSwitchingRef.current) {
      return;
    }

    commitPendingPush();

    if (historyRef.current.length === 0) {
      return;
    }

    const snapshot = historyRef.current.pop();
    if (!snapshot) return;

    setHistoryVersion((v) => v + 1);
    isApplyingUndoRef.current = true;
    isUndoingRef.current = true;
    setIsUndoing(true);

    try {
      const currentMineModelQuery = mineModelQueryRef.current;
      const currentStopeLocations = currentMineModelQuery.data?.reticulationData?.stopeLocations ?? [];
      const snapshotStopeNodeMap = getSnapshotStopeNodeMap(snapshot);
      const stopesToRemoveBeforeUndo = currentStopeLocations
        .filter((stopeLocation) => !snapshotStopeNodeMap.has(stopeLocation.stopeId));

      await Promise.all(stopesToRemoveBeforeUndo.map((stopeLocation) => deleteStopeMutationRef.current.mutateAsync({
        projectId: currentProject.entityId,
        stopeId: stopeLocation.stopeId,
        stopeName: stopeLocation.stopeId,
        nodeId: stopeLocation.nodeId,
      })));

      await updateMineModelMutationRef.current.mutateAsync({
        projectId: currentProject.entityId,
        projectName: currentProject.name,
        data: snapshot,
        // Restoring a prior state may re-route a pours-planned stope; bypass that guard for undo.
        force: true,
      });

      await currentMineModelQuery.refetch();
    } catch {
      historyRef.current.push(snapshot);
      if (historyRef.current.length > MAX_UNDO_ITEMS) {
        historyRef.current.shift();
      }
      isApplyingUndoRef.current = false;
      setHistoryVersion((v) => v + 1);
    } finally {
      isUndoingRef.current = false;
      setIsUndoing(false);
    }
  }, [commitPendingPush]);

  React.useEffect(() => {
    setUndoAction({
      enabled: canUndo,
      loading: isUndoing,
      onClick: handleUndo,
    });

    return () => {
      setUndoAction({
        enabled: false,
        loading: false,
        onClick: null,
      });
    };
  }, [canUndo, handleUndo, isUndoing, setUndoAction]);

  if (!mineModelDto || !project || !mineOverview) {
    return (
      <AlertText severity="info">
        <Trans id="The project is loading, please wait...">
          The project is loading, please wait...
        </Trans>
      </AlertText>
    );
  }

  return (
    <ProjectProvider project={project}>
      <SceneManagerProvider path={path} url={url}>
        <Stage modelState={modelState} refetchMineModel={mineModelQuery.refetch} />
      </SceneManagerProvider>
    </ProjectProvider>
  );
};

export default ProjectStopesPage;
